import React, {useEffect} from 'react';
import moment from 'moment';
import {OfficeBuildingIcon} from '@heroicons/react/solid';

import AddInterviewCard from './AddInterviewCard';
import BlogPostCard from './BlogPostCard';
import CoachCard from './CoachCard';
import ExperienceCard from './ExperienceCard';
import CourseCard from '../../components/CourseCard';
import GuideCard from './GuideCard';
import QuestionCard from './QuestionCard';
import GetReferredCard from './GetReferredCard';
import Button from '../../components/Button';
import Link from '../../components/Link';
import DocumentHeader from '../../components/DocumentHeader';
import FAQSection from '../../components/FAQSection';
import {Breadcrumb} from '../../components/LinkedData';
import {CaretIcon} from '../../components/svg';
import FollowButton, {ButtonSizes} from '../../components/FollowButton';
import {useAppContext, useAnalytics} from '../../hooks';
import {shouldHideCoaching, shouldHideJobReferrals} from '../../util';
import {getBrandName} from '../../components/Brand';

// eslint-disable-next-line jsx-a11y/heading-has-content
const H2 = (props) => <h2 className="text-lg font-semibold text-gray-900 mb-3" {...props} />;

const Grid = (props) => <div className="grid grid-cols-1 gap-3 md:grid-cols-2" {...props} />;

const SidebarGrid = (props) => <div className="grid grid-cols-1 gap-3" {...props} />;

const SeeMore = (props) => <Link className="inline-block mt-3 text-sm font-medium text-indigo hover:text-indigo-900" {...props} />;

const CompanyPage = ({
  company,
  about,
  faq,
  updatedAt,
  numReferrers,
  blogPosts, numBlogPosts,
  coaches, numCoaches,
  courses, numCourses,
  experiences, numExperiences,
  guides, numGuides,
  questions, numQuestions,
}) => {
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();

  useEffect(() => {
    tracker.hubView({tag: company.id});
  }, []);

  const emptyMainSection = !(
    guides?.length || experiences?.length || courses?.length || questions?.length
  );
  const showSidebar = Boolean(about || coaches?.length || blogPosts?.length);
  const title = `${company.name} Interview Process (${new Date().getFullYear()})`;
  // e.g. ["4 interview guides", "120 interview questions", "1 candidate experience"]
  const counts = [
    [numGuides, 'interview guide'],
    [numQuestions, 'interview question'],
    [numExperiences, 'candidate experience'],
  ]
    .filter(([count]) => count > 0)
    .map(([count, noun]) => `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`);
  const description = [
    counts.length
      ? `Prepare for your ${company.name} interview with ${new Intl.ListFormat('en').format(counts)} from ${getBrandName()}.`
      : `Prepare for your ${company.name} interview with ${getBrandName()}'s expertise on their interview process, sample questions, and tips from previous interviewers.`,
    updatedAt && `Updated ${moment.utc(updatedAt).format('MMMM YYYY')}.`,
  ].filter(Boolean).join(' ');

  const onClickFollowButton = (following) => (following
    ? tracker.hubFollowTag({follower: currentUser.id, tag: company.id})
    : tracker.hubUnfollowTag({follower: currentUser.id, tag: company.id})
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Metadata */}
      <DocumentHeader
        title={title}
        metaDescription={description}
        socialDescription={description}
      />
      <Breadcrumb items={[
        {name: 'Companies', url: '/companies'},
        {name: company.name, url: `/companies/${company.id}`},
      ]}
      />

      <div className="px-4 lg:px-8 flex justify-center">
        <div className="min-w-0 flex-1 max-w-2xl py-5 md:py-10">

          {/* Header */}
          <header className="grid grid-cols-[auto_minmax(0,1fr)] md:grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-4 mb-8">
            {company.image_url ? (
              <img src={company.image_url} alt={`${company.name} logo`} className="row-span-2 h-10 w-10 md:h-14 md:w-14 object-contain" />
            ) : (
              <OfficeBuildingIcon className="row-span-2 h-10 w-10 md:h-14 md:w-14 text-gray-300" />
            )}
            <div className="flex items-center space-x-2">
              <CaretIcon height={9} width={6} fill="#999" />
              <Link href="/companies" className="text-sm font-medium text-gray-500 hover:text-gray-700">Companies</Link>
            </div>
            <h1 className="col-start-2 mt-0.5 text-xl md:text-2xl max-w-2xl font-bold tracking-tight text-gray-900">{title}</h1>
            <FollowButton
              tag={company}
              size={ButtonSizes.LARGE}
              onClick={onClickFollowButton}
              className="col-span-2 w-full mt-4 md:col-span-1 md:col-start-3 md:row-start-2 md:w-auto md:mt-0"
            />
          </header>

          {/* Intro */}
          <p className="-mt-4 mb-8 text-base text-gray-500">{description}</p>

          {/* Mobile-only about */}
          {about && (
            <div className="block lg:hidden mb-8">
              <H2>{`About ${company.name}`}</H2>
              <p className="text-base leading-relaxed text-gray-500 max-w-xl">{about}</p>
            </div>
          )}

          {/* Empty main section placeholder */}
          {emptyMainSection && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 flex justify-between items-center flex-col mb-8">
              <div className="text-base font-medium text-gray-900 mb-4 max-w-xl sm:text-center">
                {`We don't know that much about ${company.name} yet. Share your interview experience and help get the conversation started!`}
              </div>
              <Button
                href="/questions/contribute"
                onClick={() => tracker.hubClickContribute()}
                className="shrink-0 w-full sm:w-auto"
              >
                Add interview
              </Button>
            </div>
          )}

          {/* Guides */}
          {guides?.length > 0 && (
            <div className="mb-8">
              <H2>{`${company.name} Interview Guides`}</H2>
              <Grid>
                {guides.map((guide) => <GuideCard key={guide.slug} guide={guide} tag={company} />)}
              </Grid>
              {numGuides > guides.length && (
                <SeeMore
                  href={`/guides?company=${company.id}`}
                  onClick={() => tracker.hubSeeMore({type: 'guides', tag: company.id})}
                >
                  See all guides
                </SeeMore>
              )}
            </div>
          )}

          {/* Interview Experiences */}
          {experiences?.length > 0 && (
            <div className="mb-8">
              <H2>{`${company.name} Candidate Interview Experiences`}</H2>
              <Grid>
                {experiences.map((experience) => (
                  <ExperienceCard key={experience.id} experience={experience} tag={company} />
                ))}
              </Grid>
              {numExperiences > experiences.length && (
                <SeeMore
                  href={`/experiences?company=${company.id}`}
                  onClick={() => tracker.hubSeeMore({type: 'experiences', tag: company.id})}
                >
                  See all interview experiences
                </SeeMore>
              )}
            </div>
          )}

          {/* Job Referrals */}
          {numReferrers > 0 && !shouldHideJobReferrals(currentUser) && (
            <GetReferredCard
              tag={company}
              numReferrers={numReferrers}
              className="mb-8"
            />
          )}


          {/* Courses */}
          {courses?.length > 0 && (
            <div className="mb-8">
              <H2>Courses</H2>
              <Grid>
                {courses.map((course) => (
                  <CourseCard
                    key={course.slug}
                    course={course}
                    trackerEvent={() => tracker.hubClickCard({type: 'courses', tag: company.id, slug: course.slug})}
                    urlParams={{src: 'blog'}}
                  />
                ))}
              </Grid>
              {numCourses > courses.length && (
                <SeeMore
                  href={`/courses?tag=${company.name}`}
                  onClick={() => tracker.hubSeeMore({type: 'courses', tag: company.id})}
                >
                  See all courses
                </SeeMore>
              )}
            </div>
          )}

          {/* Questions */}
          {questions?.length > 0 && (
            <div className="mb-8">
              <H2>{`${company.name} Interview Questions`}</H2>
              <div className="space-y-2">
                {questions.map((question) => (
                  <QuestionCard key={question.id} question={question} tag={company} />
                ))}
              </div>
              <AddInterviewCard tag={company} className="mt-3" />
              {numQuestions > questions.length && (
                <SeeMore
                  href={`/questions?company=${company.id}`}
                  onClick={() => tracker.hubSeeMore({type: 'questions', tag: company.id})}
                >
                  See all questions
                </SeeMore>
              )}
            </div>
          )}

          {/* Mobile-only coaches */}
          {coaches?.length > 0 && !shouldHideCoaching(currentUser) && (
            <div className="block lg:hidden mb-8">
              <H2>{`Top ${company.name} Coaches`}</H2>
              <Grid>
                {coaches.map((coach) => <CoachCard key={coach.slug} coach={coach} tag={company} />)}
              </Grid>
              {numCoaches > coaches.length && (
                <SeeMore
                  href={`/coaching?company=${company.id}`}
                  onClick={() => tracker.hubSeeMore({type: 'coaching', tag: company.id})}
                >
                  See all coaches
                </SeeMore>
              )}
            </div>
          )}

          {/* Mobile-only blog posts */}
          {blogPosts?.length > 0 && (
            <div className="block lg:hidden mb-8">
              <H2>Related Blog Posts</H2>
              <Grid>
                {blogPosts.map((post) => (
                  <BlogPostCard key={post.slug} post={post} tag={company} />
                ))}
              </Grid>
              {numBlogPosts > blogPosts.length && (
                <SeeMore
                  href={`/blog/tag/${company.id}`}
                  onClick={() => tracker.hubSeeMore({type: 'blog', tag: company.id})}
                >
                  See all blog posts
                </SeeMore>
              )}
            </div>
          )}

          {/* FAQ */}
          {faq?.length > 0 && (
            <div className="mb-8">
              <H2>{`${company.name} Interview FAQ`}</H2>
              <FAQSection items={faq} includeMetadata className="mt-4" />
            </div>
          )}
        </div>

        {/* Sidebar */}
        {showSidebar && (
          <div className="border-l border-gray-200 ml-8 pl-8 py-8 md:py-10 w-80 shrink-0 hidden lg:block">

            {/* About */}
            {about && (
              <div className="mb-8">
                <H2>{`About ${company.name}`}</H2>
                <p className="text-base leading-relaxed text-gray-500 max-w-xl">{about}</p>
              </div>
            )}

            {/* Coaches */}
            {coaches?.length > 0 && !shouldHideCoaching(currentUser) && (
              <div className="mb-8">
                <H2>{`Top ${company.name} Coaches`}</H2>
                <SidebarGrid>
                  {coaches.map((coach) => (
                    <CoachCard key={coach.slug} coach={coach} tag={company} />
                  ))}
                </SidebarGrid>
                {numCoaches > coaches.length && (
                  <SeeMore
                    href={`/coaching?company=${company.id}`}
                    onClick={() => tracker.hubSeeMore({type: 'coaching', tag: company.id})}
                  >
                    See all coaches
                  </SeeMore>
                )}
              </div>
            )}

            {/* Blog posts */}
            {blogPosts?.length > 0 && (
              <div className="mb-8">
                <H2>Related Blog Posts</H2>
                <SidebarGrid>
                  {blogPosts.map((post) => (
                    <BlogPostCard key={post.slug} post={post} tag={company} />
                  ))}
                </SidebarGrid>
                {numBlogPosts > blogPosts.length && (
                  <SeeMore
                    href={`/blog/tag/${company.id}`}
                    onClick={() => tracker.hubSeeMore({type: 'blog', tag: company.id})}
                  >
                    See all blog posts
                  </SeeMore>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyPage;
